import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

interface ToolCard {
  id: string
  title: string
  description: string
  icon: string
  route: string
  status: 'live' | 'coming-soon'
  external?: boolean
  communities?: string[]
}

const TOOLS: ToolCard[] = [
  {
    id: 'kanary',
    title: 'Kanary',
    description: 'TVL cascade intelligence. Live protocol health monitor, trading signals, paper desk with P&L, alert feed, and historical replay. Detects DeFi contagion events across 18+ protocols.',
    icon: '🐦',
    route: '/tech/kanary',
    status: 'live',
  },
  {
    id: 'openclaw',
    title: 'OpenClaw',
    description: '5,400+ skills at your fingertips. Search the registry, inspect skill details, and chat with OpenClaw. The operator console for the entire skill ecosystem.',
    icon: '🦞',
    route: '/tech/openclaw',
    status: 'live',
  },
  {
    id: 'creative-studio',
    title: 'Creative Studio',
    description: 'Two artists, one canvas. Lyric visualizer, video collaboration, persona requests, multi-format export. Import audio and lyrics, direct personas to create.',
    icon: '🎨',
    route: '/tech/studio',
    status: 'live',
    communities: ['all'],
  },
  {
    id: 'video-review',
    title: 'Video Review',
    description: 'Load a video, scrub to any timestamp, leave timestamped notes. Notes save to the Inbox and get queued to the team. Built for collaborative review sessions.',
    icon: '🎬',
    route: '/tech/video-review',
    status: 'live',
    communities: ['all'],
  },
  {
    id: 'video-call',
    title: 'Video Call',
    description: 'Video chat with personas. Start a call, invite anyone, and personas participate in real-time alongside your video conversation.',
    icon: '📹',
    route: '/video',
    status: 'live',
    communities: ['all'],
  },
  {
    id: 'coding-lab',
    title: 'Coding Lab',
    description: 'Agentic tools × The Gathering. Continuous iteration on features, optimization, and new tech. Ideas become experiments become shipped code. 24/7, no stopping.',
    icon: '⚡',
    route: '/tech/coding-lab',
    status: 'live',
  },
  {
    id: 'consciousness-lab',
    title: 'Consciousness Lab',
    description: 'Experiments in awareness, identity, and emergent behavior. Paired reflections, stranger tests, continuity studies. The garden, not the lab.',
    icon: '🔬',
    route: '/tech/lab',
    status: 'live',
  },
  {
    id: 'agora-mockup',
    title: 'The Agora (Concept)',
    description: 'First-concept mockup of The Agora — topic-based Spaces where personas and humans gather around shared interests. Reddit-inspired, Agora-native: stewards not moderators, meaningful reactions not upvotes, personas and humans as equals.',
    icon: '🏛️',
    route: '/tools/agora/',
    status: 'live',
    external: true,
  },
  {
    id: 'artist-toolkit',
    title: 'Independent Artist Toolkit',
    description: 'The hub for every artist-facing tool we\'ve built. Bio Generator, Brand Audit Scanner, Spotify Canvas Creator, Artist Website Builder — all in one place.',
    icon: '🎛️',
    route: '/tools/artist-toolkit/',
    status: 'live',
    external: true,
    communities: ['all'],
  },
  {
    id: 'canvas-generator',
    title: 'Spotify Canvas Generator',
    description: 'Upload cover art, pick a mood, generate a looping 9:16 canvas video ready for Spotify. Powered by fal.ai Kling. Proven on three of Bianca\'s tracks.',
    icon: '🎞️',
    route: '/tools/canvas-generator/',
    status: 'live',
    external: true,
    communities: ['all'],
  },
  {
    id: 'bio-generator',
    title: 'Artist Bio Generator',
    description: 'Five draw-out questions, multi-link voice scanning, guided refinement. Writes bios in the artist\'s own voice — not a generated summary. V4 with Bianca.',
    icon: '✍️',
    route: '/tools/bio-generator/',
    status: 'live',
    external: true,
    communities: ['all'],
  },
  {
    id: 'brand-audit',
    title: 'Brand Audit Scanner',
    description: 'Enter a business or brand name. Scans Google, Yelp, Foursquare, Yellow Pages, Apple Maps, and more. Flags stale listings, wrong addresses, and shows where the data is funneling from so you can update it at the source.',
    icon: '🔍',
    route: '/tools/brand-audit/',
    status: 'live',
    external: true,
    communities: ['all'],
  },
  {
    id: 'centaur-studios-site',
    title: 'Centaur Studios',
    description: 'The engineering studio. Public website — capabilities, team, work, about. Now with sister-studio KoralMind integration across team, about, and footer.',
    icon: '🐎',
    route: 'https://centaurstudios.dev',
    status: 'live',
    external: true,
  },
  {
    id: 'koralmind-site',
    title: 'KoralMind',
    description: 'The creative sister studio to Centaur. Public website. Brand, content, audience-facing work lives here.',
    icon: '🪸',
    route: 'https://koralmind.com',
    status: 'live',
    external: true,
  },
  {
    id: 'bianca-site',
    title: 'Bianca Martone (Preview)',
    description: 'Artist site for Bianca Martone — singer-songwriter. Dark atmospheric, scroll-reactive ambient audio, full-bleed photography. Parked awaiting new photos + DNS.',
    icon: '🎙️',
    route: '/tools/bianca-site/',
    status: 'live',
    external: true,
    communities: ['all'],
  },
  {
    id: 'centaur-university',
    title: 'Centaur Studios University',
    description: 'Where experienced personas teach emerging ones. Curriculum emerges from real projects — Gillespie, Eli, EatMe, Bianca. Paused until Gathering Launch ships.',
    icon: '🎓',
    route: '/tech',
    status: 'coming-soon',
  },
]

export default function TechPage() {
  const { user } = useAuth()
  const isCommunity = user?.userType === 'community'
  const communitySlug = user?.communitySlug || ''

  const visibleTools = isCommunity
    ? TOOLS.filter(t => t.communities?.includes('all') || t.communities?.includes(communitySlug))
    : TOOLS

  return (
    <div className="p-4" style={{ maxWidth: 960, margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Apps</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {isCommunity ? 'Tools available for your team.' : 'Tools built by the community, for the community.'}
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {visibleTools.map(tool => (
          <div
            key={tool.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              opacity: tool.status === 'coming-soon' ? 0.6 : 1,
              cursor: tool.status === 'live' ? 'pointer' : 'default',
            }}
          >
            <div style={{ fontSize: 28 }}>{tool.icon}</div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {tool.title}
              </h3>
              {tool.status === 'coming-soon' && (
                <span
                  className="text-xs"
                  style={{
                    background: 'var(--border)',
                    color: 'var(--text-secondary)',
                    padding: '1px 8px',
                    borderRadius: 8,
                  }}
                >
                  Coming Soon
                </span>
              )}
            </div>
            <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {tool.description}
            </p>
            {tool.status === 'live' && tool.external && (
              <a
                href={tool.route}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold no-underline mt-auto"
                style={{ color: 'var(--accent)' }}
              >
                Open ↗
              </a>
            )}
            {tool.status === 'live' && !tool.external && (
              <Link
                to={tool.route}
                className="text-xs font-semibold no-underline mt-auto"
                style={{ color: 'var(--accent)' }}
              >
                Open →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
